// Ejected mass (architecture §9.2 step 5, art-assets §5, visuals §8), in the ejecting player's
// colour (the ADD colour), culled to the screen, drawn in the body list sorted by drawn radius:
// - classic: a mini cell, the same 10 u two-fill band as cells (outline × 0.9) on a jelly polygon
//   (N from the cell rule, min 16 ≈ 35–37 vertices at zoom 1), or two circles without jelly;
// - sketch: a solid fill with an ink outline (chalk in dark mode; no boil);
// - `world/ejected.png` (when present): multiply-tinted with the colour, side = 2.4 × r.
// "Plain colors" draws ejected mass in the plain grey too.

import { classicOutlineColor, type Rgb } from '@ballio/shared';
import type { AssetImage } from '../assets/loader.js';
import { circleInRect } from './cull.js';
import type { Frame } from './frame.js';
import { BAND_U, fillBand, sketchStrokePx, traceCircle } from './outline.js';
import type { SpriteCache } from './sprite-cache.js';
import { css } from './theme.js';
import type { BodyShape } from './viruses.js';

/** Image side = k × r for ejected art (art-assets §5). */
export const EJECTED_ART_K = 2.4;

/** True when an ejected blob at `shape` is on screen (bounding box with its band or art). */
export function ejectedVisible(f: Frame, shape: BodyShape, art: AssetImage | null): boolean {
  const reach = art ? shape.r * (EJECTED_ART_K / 2) : shape.r + BAND_U / 2;
  return shape.r > 0.3 / f.zoom && circleInRect(shape.x, shape.y, reach, f.view);
}

/**
 * One ejected blob (world transform). `color` is its ADD colour (the caller applies "Plain
 * colors"); `off`/`n` are classic jelly offsets or null. Counts `stats.ejected`.
 */
export function drawEjectedOne(
  f: Frame,
  shape: BodyShape,
  color: Rgb,
  off: Float64Array | null,
  n: number,
  art: AssetImage | null,
  tint: boolean,
  sprites: SpriteCache,
): void {
  const { ctx, zoom } = f;
  const { x, y, r } = shape;
  f.stats.ejected++;
  if (art) {
    const half = r * (EJECTED_ART_K / 2);
    const img = tint ? sprites.tinted(art, color) : art;
    if (img) {
      ctx.drawImage(img, x - half, y - half, half * 2, half * 2);
      return;
    }
  }
  if (f.theme.style !== 'sketch') {
    fillBand(ctx, x, y, r, off, n, css(color), css(classicOutlineColor(color)), BAND_U);
    return;
  }
  const lw = sketchStrokePx(r * zoom) / zoom;
  ctx.beginPath();
  traceCircle(ctx, x, y, Math.max(0, r - lw / 2));
  ctx.fillStyle = css(color);
  ctx.fill();
  ctx.lineWidth = lw;
  ctx.strokeStyle = css(f.theme.colors.border);
  ctx.stroke();
}
